import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesStore, localizeArticle } from "@/store/articlesStore";
import type { ManagedArticle } from "@/store/articlesStore";
import { useInView } from "@/hooks/useInView";
import { useCategoryLocalize } from "@/hooks/useCategoryLocalize";
import { onRealtimeEvent } from "@/lib/realtimeManager";
import { throttle } from "@/lib/throttle";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getSectionFlags } from "@/store/siteSettingsStore";
import { formatArticleDate } from "@/lib/dateFormatter";
import { latestNews } from "@/mocks/articles";

export default function TrendingNews() {
  const { t, i18n } = useTranslation();
  const localizeCat = useCategoryLocalize();
  const [trending, setTrending] = useState<ManagedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionRef] = useInView<HTMLElement>({ rootMargin: "400px", fallbackTimeout: 800 });
  const { settings } = useSiteSettings();
  const flags = getSectionFlags(settings?.articleDisplayFlags, "trendingNews");

  const load = useCallback(async () => {
    setLoading(true);

    // سلسلة fallback: trending flagged → most viewed → published → mock
    let data: ManagedArticle[] = [];

    try {
      data = await articlesStore.getTrending();
    } catch {
      data = [];
    }

    if (data.length < 3) {
      try {
        const mostViewed = await articlesStore.getMostViewed(5);
        if (mostViewed.length > 0) data = mostViewed;
      } catch {
        // ignore
      }
    }

    if (data.length < 3) {
      try {
        const published = await articlesStore.getPublishedAndFeatured(5);
        if (published.length > 0) data = published;
      } catch {
        // ignore
      }
    }

    if (data.length < 3) {
      // último recurso: mock data من الملف المحلي
      const mock = latestNews.slice(0, 5).map((m) => ({
        id: m.id,
        slug: m.slug,
        category: m.category,
        categorySlug: m.categorySlug ?? "",
        title: m.title,
        excerpt: m.excerpt,
        author: m.author,
        authorTitle: m.authorTitle ?? "",
        authorImage: m.authorImage ?? "",
        date: m.date,
        readTime: m.readTime ?? "",
        views: m.views ?? "",
        image: m.image,
        thumbnailImage: m.thumbnailImage,
        tags: m.tags ?? [],
        featured: m.featured ?? false,
        images: m.images ?? [],
        videos: m.videos ?? [],
        imagePlacement: m.imagePlacement ?? "bottom",
        status: "published" as const,
        secondaryCategory: m.secondaryCategory,
        secondaryCategorySlug: m.secondaryCategorySlug,
        views_count: 0,
        likes_count: 0,
      })) as ManagedArticle[];
      data = mock;
    }

    setTrending(data.map((article) => localizeArticle(article, i18n.language)).slice(0, 5));
    setLoading(false);
  }, [i18n.language]);

  useEffect(() => {
    load();
    const throttledLoad = throttle(load, 2000);
    const unsub = onRealtimeEvent("alsiyahiah:articles:updated", throttledLoad);
    return unsub;
  }, [load]);

  const [main, ...rest] = trending;

  if (loading) {
    return (
      <section ref={sectionRef} className="max-w-screen-xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-[#D4AF37] rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Cairo']">
              {t("trending.title")}
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 aspect-[16/10] bg-gray-100 rounded-2xl animate-pulse" />
          <div className="lg:col-span-2 flex flex-col gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3 bg-gray-50 rounded-xl p-3 h-20 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!main) return <section ref={sectionRef} />;

  const formatViews = (v: string | number | undefined) => {
    if (!v) return "0";
    if (typeof v === "number") return v.toLocaleString("en-US");
    return v;
  };

  return (
    <section ref={sectionRef} className="max-w-screen-xl mx-auto px-4 md:px-8 py-16">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-1 h-7 bg-[#D4AF37] rounded-full" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Cairo']">
            {t("trending.title")}
          </h2>
          {flags?.showViews !== false && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
              <i className="ri-bar-chart-line text-[#D4AF37]" />
              {t("trending.sortedByViews")}
            </span>
          )}
        </div>
        <Link
          to="/search"
          className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap border border-gray-200 px-4 py-2 rounded-full hover:border-[#D4AF37]"
        >
          {t("trending.viewAll")}
          <i className="ri-arrow-left-s-line" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Main article */}
        <Link
          to={`/article/${main.slug}`}
          className="lg:col-span-3 relative group cursor-pointer overflow-hidden rounded-2xl"
        >
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={main.image}
              alt={main.title}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 rounded-2xl" />

          <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-[#D4AF37] rounded-full text-[#0A1628] font-black text-base">
            1
          </div>

          <div className="absolute bottom-0 right-0 left-0 p-6">
            {flags?.showCategory !== false && (
              <span className="inline-block bg-[#D4AF37] text-[#0A1628] text-xs font-bold px-2.5 py-1 rounded-full mb-3">
                {localizeCat(main.categorySlug, main.category)}
              </span>
            )}
            <h3 className="text-white font-bold text-xl md:text-2xl leading-snug mb-3 font-['Cairo'] group-hover:text-[#D4AF37] transition-colors">
              {main.title}
            </h3>
            <p className="text-white/65 text-sm leading-relaxed mb-4 font-['Cairo'] line-clamp-2">
              {main.excerpt}
            </p>
            <div className="flex items-center gap-3 text-white/55 text-xs">
              {flags?.showAuthor !== false && (
                <span className="flex items-center gap-1"><i className="ri-user-3-line" />{main.author}</span>
              )}
              {flags?.showReadTime !== false && (
                <><span>·</span><span className="flex items-center gap-1"><i className="ri-time-line" />{main.readTime}</span></>
              )}
              {flags?.showViews !== false && (
                <><span>·</span><span className="flex items-center gap-1 text-[#D4AF37]/80 font-semibold">
                  <i className="ri-eye-line" />
                  {main.views_count != null && main.views_count > 0
                    ? main.views_count.toLocaleString("en-US")
                    : formatViews(main.views)}
                </span></>
              )}
            </div>
          </div>
        </Link>

        {/* Side articles */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          {rest.map((article, idx) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="flex gap-3 group cursor-pointer bg-white hover:bg-[#FAFAFA] rounded-xl p-3 transition-colors border border-gray-100 hover:border-[#D4AF37]/30"
            >
              <div className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-black text-sm">
                {idx + 2}
              </div>

              <div className="w-20 h-16 rounded-lg overflow-hidden shrink-0">
                <img
                  src={article.thumbnailImage || article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <div className="flex-1 min-w-0">
                {flags?.showCategory !== false && (
                  <span className="text-[#D4AF37] text-xs font-semibold">{localizeCat(article.categorySlug, article.category)}</span>
                )}
                <h4 className="text-gray-800 text-sm font-semibold leading-snug mt-0.5 line-clamp-2 group-hover:text-[#0A1628] transition-colors font-['Cairo']">
                  {article.title}
                </h4>
                <div className="flex items-center gap-2 text-gray-400 text-xs mt-1.5">
                  {flags?.showViews !== false && (
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {article.views_count != null && article.views_count > 0
                        ? article.views_count.toLocaleString("en-US")
                        : formatViews(article.views)}
                    </span>
                  )}
                  {flags?.showReadTime !== false && (
                    <><span>·</span><i className="ri-time-line" />{article.readTime}</>
                  )}
                  {flags?.showPublishDate !== false && (
                    <><span>·</span><span>{formatArticleDate(article.date, i18n.language)}</span></>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
